import React, { useCallback, useEffect, useId, useRef, useState } from 'react'
import { useTranslation, useCurrentLanguage } from '../hooks/useTranslation'
import { getSiteUrl } from '../lib/siteUrl.js'

function filenameFromImagePath (imagePath, groupId) {
  const base = imagePath?.split('/').pop()
  if (base) return base
  return `viva-group-${groupId || 'flyer'}.jpg`
}

function buildGroupPageUrl (language, groupId) {
  // Always use the public site URL so WhatsApp/Facebook can scrape Open Graph
  // (localhost links never get a preview card).
  const origin = getSiteUrl()
  const prefix = language === 'pt' ? '/pt' : language === 'en' ? '/en' : ''
  return `${origin}${prefix}/groups/${groupId}`
}

function buildShareMessage (group, pageUrl) {
  return [`*${group.title}*`, group.tagline, group.dates, '', pageUrl].filter(Boolean).join('\n')
}

async function copyText (text) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text)
    return
  }
  const textarea = document.createElement('textarea')
  textarea.value = text
  textarea.setAttribute('readonly', '')
  textarea.style.position = 'fixed'
  textarea.style.left = '-9999px'
  document.body.appendChild(textarea)
  textarea.select()
  document.execCommand('copy')
  document.body.removeChild(textarea)
}

async function downloadImage (imagePath, filename) {
  const response = await fetch(imagePath)
  if (!response.ok) throw new Error('download failed')
  const blob = await response.blob()
  const objectUrl = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = objectUrl
  anchor.download = filename
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  URL.revokeObjectURL(objectUrl)
}

const iconBtnClass =
  'group relative inline-flex h-10 w-10 items-center justify-center rounded-lg border border-primary-darkGreen/20 bg-white text-primary-darkGreen shadow-sm transition hover:border-primary-darkGreen/40 hover:bg-[#f7f3ec] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-golden focus-visible:ring-offset-2'

const tooltipClass =
  'pointer-events-none absolute left-1/2 top-full z-30 mt-2 -translate-x-1/2 whitespace-nowrap rounded-md bg-gray-900 px-2.5 py-1 text-xs font-medium text-white opacity-0 shadow-lg transition-opacity duration-150 group-hover:opacity-100 group-focus-visible:opacity-100'

const menuItemClass =
  'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium text-gray-800 transition hover:bg-[#f7f3ec] focus:outline-none focus-visible:bg-[#f7f3ec]'

/**
 * Icon-only download + share actions (tooltips for labels).
 * @param {{ group: object }} props
 */
export default function GroupShareActions ({ group }) {
  const t = useTranslation()
  const language = useCurrentLanguage()
  const menuId = useId()
  const rootRef = useRef(null)
  const [menuOpen, setMenuOpen] = useState(false)
  const [status, setStatus] = useState('idle')

  const pageUrl = buildGroupPageUrl(language, group.id)
  const shareMessage = buildShareMessage(group, pageUrl)
  const filename = filenameFromImagePath(group.image, group.id)
  const downloadLabel = t('groups.shared.download')
  const shareLabel = t('groups.shared.share')

  useEffect(() => {
    if (!menuOpen) return
    const onPointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setMenuOpen(false)
      }
    }
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setMenuOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [menuOpen])

  const flashStatus = useCallback((next) => {
    setStatus(next)
    window.setTimeout(() => setStatus('idle'), 2200)
  }, [])

  const handleDownload = useCallback(async () => {
    try {
      await downloadImage(group.image, filename)
      flashStatus('downloaded')
    } catch {
      window.open(group.image, '_blank', 'noopener,noreferrer')
      flashStatus('error')
    }
  }, [flashStatus, filename, group.image])

  const handleWhatsApp = useCallback(() => {
    const url = `https://wa.me/?text=${encodeURIComponent(shareMessage)}`
    window.open(url, '_blank', 'noopener,noreferrer')
    setMenuOpen(false)
  }, [shareMessage])

  const handleEmail = useCallback(() => {
    const subject = encodeURIComponent(`${group.title} | VIVA Psychotherapy`)
    const body = encodeURIComponent(shareMessage)
    window.location.href = `mailto:?subject=${subject}&body=${body}`
    setMenuOpen(false)
  }, [group.title, shareMessage])

  const handleSms = useCallback(() => {
    const body = encodeURIComponent(shareMessage)
    const href = /iPhone|iPad|iPod/i.test(navigator.userAgent)
      ? `sms:&body=${body}`
      : `sms:?body=${body}`
    window.location.href = href
    setMenuOpen(false)
  }, [shareMessage])

  const handleNativeOrCopy = useCallback(async () => {
    try {
      if (typeof navigator !== 'undefined' && navigator.share) {
        const shareData = {
          title: `${group.title} | VIVA Psychotherapy`,
          text: shareMessage,
          url: pageUrl,
        }
        if (!navigator.canShare || navigator.canShare(shareData)) {
          await navigator.share(shareData)
          flashStatus('shared')
          setMenuOpen(false)
          return
        }
      }
      await copyText(`${shareMessage}`)
      flashStatus('copied')
      setMenuOpen(false)
    } catch (err) {
      if (err?.name === 'AbortError') {
        setMenuOpen(false)
        return
      }
      try {
        await copyText(shareMessage)
        flashStatus('copied')
      } catch {
        flashStatus('error')
      }
      setMenuOpen(false)
    }
  }, [flashStatus, group.title, pageUrl, shareMessage])

  const handleCopyLink = useCallback(async () => {
    try {
      await copyText(pageUrl)
      flashStatus('copied')
      setMenuOpen(false)
    } catch {
      flashStatus('error')
    }
  }, [flashStatus, pageUrl])

  const statusLabel =
    status === 'downloaded'
      ? t('groups.shared.downloadDone')
      : status === 'copied'
        ? t('groups.shared.copyDone')
        : status === 'shared'
          ? t('groups.shared.shareDone')
          : status === 'error'
            ? t('groups.shared.actionError')
            : null

  return (
    <div ref={rootRef} className="relative flex shrink-0 items-center gap-1.5">
      <button
        type="button"
        onClick={handleDownload}
        className={iconBtnClass}
        aria-label={downloadLabel}
      >
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
          />
        </svg>
        <span className={tooltipClass} role="tooltip">
          {downloadLabel}
        </span>
      </button>

      <div className="relative">
        <button
          type="button"
          className={iconBtnClass}
          aria-label={shareLabel}
          aria-expanded={menuOpen}
          aria-controls={menuId}
          aria-haspopup="menu"
          onClick={() => setMenuOpen((open) => !open)}
        >
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
            />
          </svg>
          <span className={tooltipClass} role="tooltip">
            {shareLabel}
          </span>
        </button>

        {menuOpen ? (
          <div
            id={menuId}
            role="menu"
            className="absolute right-0 top-full z-40 mt-2 w-56 rounded-xl border border-primary-darkGreen/15 bg-white p-2 shadow-lg shadow-black/10"
          >
            <button type="button" role="menuitem" className={menuItemClass} onClick={handleWhatsApp}>
              <svg className="h-4 w-4 shrink-0 text-[#25D366]" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.435 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
              </svg>
              {t('groups.shared.shareWhatsApp')}
            </button>
            <button type="button" role="menuitem" className={menuItemClass} onClick={handleEmail}>
              <svg className="h-4 w-4 shrink-0 text-primary-darkGreen" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              {t('groups.shared.shareEmail')}
            </button>
            <button type="button" role="menuitem" className={menuItemClass} onClick={handleSms}>
              <svg className="h-4 w-4 shrink-0 text-primary-darkGreen" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              {t('groups.shared.shareSms')}
            </button>
            <button type="button" role="menuitem" className={menuItemClass} onClick={handleNativeOrCopy}>
              <svg className="h-4 w-4 shrink-0 text-primary-darkGreen" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
              {t('groups.shared.shareMore')}
            </button>
            <button type="button" role="menuitem" className={menuItemClass} onClick={handleCopyLink}>
              <svg className="h-4 w-4 shrink-0 text-primary-darkGreen" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
              {t('groups.shared.copyLink')}
            </button>
          </div>
        ) : null}
      </div>

      {statusLabel ? (
        <span className="sr-only" role="status" aria-live="polite">
          {statusLabel}
        </span>
      ) : null}
    </div>
  )
}
